import { Avatar, List, ListItem, ListItemAvatar, ListItemText, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as api from 'api/Core';
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiPagination from 'components/MuiExtras/MuiPagination';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import UserFullProfileLink from 'components/UserFullProfileLink';
import { PagedResult, UserShortView } from 'models/ApiModels';
import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    ds: PagedResult<UserShortView>;
    offset: number;
    loading: boolean;
    lastError: any;
    searchText: string;
}

interface IProps {
    userId: string;
    username: string;
    onClose: () => void;
}

type Props = IProps & WithTranslation;

const rowsPerPage = 20;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class UserFollowersDrawer extends React.PureComponent<Props, IState> {

    private searchRef: any = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            ds: null,
            offset: 0,
            loading: true,
            lastError: null,
            searchText: ""
        };
    }

    private getData(offset: number) {
        this.setState({ loading: true });

        api.getUserFollowers(this.props.userId, this.state.searchText, { take: rowsPerPage, offset })
            .then((result) => {
                this.setState({ ds: result, loading: false, lastError: null, offset: offset });
            })
            .catch((error) => {
                this.setState({ ds: null, lastError: error, loading: false, offset: 0 });
            });
    }

    private handleSearch(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.keyCode == 13) this.getData(0);
    }

    private renderContent() {
        const { ds, loading } = this.state;

        return (
            <GridEx container fullHeight direction="column" wrap="nowrap">
                <GridEx>

                    <MuiContainer>
                        <TextField
                            fullWidth
                            placeholder="Pesquisar…"
                            value={this.state.searchText}
                            onChange={() => this.setState({ searchText: this.searchRef.value })}
                            onKeyDown={(e) => this.handleSearch(e)}
                            onBlur={() => this.getData(0)}
                            inputRef={(ref) => this.searchRef = ref}
                            InputProps={
                                {
                                    startAdornment: <SearchIcon />,
                                    endAdornment: this.state.searchText != ""
                                        ? <IconButtonCancel
                                            size="small"
                                            onClick={
                                                () => {
                                                    this.setState({ searchText: "" }, () => this.getData(0));
                                                }
                                            } />
                                        : null
                                }
                            }
                        />
                    </MuiContainer>

                </GridEx>
                <GridEx item xs overflow="auto">
                    {
                        loading
                            ? <SpinnerIndicator />
                            : (
                                <List dense>
                                    {
                                        ds.items.map((v, index) => {
                                            return (
                                                <ListItem key={index} disableGutters >
                                                    <ListItemAvatar>
                                                        <Avatar src={v.thumbnailUrl} />
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={<UserFullProfileLink user={v} color="secondary" />}
                                                        primaryTypographyProps={{ noWrap: true }}
                                                    />
                                                </ListItem>
                                            );
                                        })
                                    }
                                </List>
                            )
                    }
                </GridEx>

                {
                    !loading && ds.total > rowsPerPage &&
                    <GridEx item>
                        <MuiPagination
                            limit={rowsPerPage}
                            offset={this.state.offset}
                            total={ds.total}
                            onClick={(e, offset) => this.getData(offset)}
                        />
                    </GridEx>
                }
            </GridEx>
        );
    }

    public componentDidMount() {
        this.getData(0);
    }

    public render() {
        return (
            <BoxDrawer
                open
                overflow="hidden"
                onClose={() => this.props.onClose()}
                title={this.props.t("Views.User.Dialogs.UserFollowersDrawer.Title")}
                subtitle={this.props.username}
            >
                <MuiPaper fullHeight elevation={0}>
                    {this.renderContent()}
                </MuiPaper>
            </BoxDrawer>
        );
    }
}

export default withTranslation()(UserFollowersDrawer);